/**
 * The one audio element a Listening paper plays through, and the click that
 * unlocks it.
 *
 * A browser lets a page start sound only from a person's click, and the
 * strict ones — Safari, Safe Exam Browser, the iPad, and Chrome after a
 * reload — mean the click itself, not the page having been clicked at some
 * point. The recording begins a moment after "Start the test", once the paper
 * has loaded, which is too late for them: the candidate met "your browser
 * blocked the sound" on every paper.
 *
 * So the element exists before the paper does. The candidate's own clicks on
 * the opening screen play a fraction of a second of silence on it, which is
 * what a browser remembers: an element a click has started may play whatever
 * it is given afterwards. The paper then plays its recording on that same
 * element.
 */

let element: HTMLAudioElement | null = null;
let silence: string | null = null;

export function examAudio(): HTMLAudioElement {
  if (!element) {
    element = new Audio();
    element.preload = 'auto';
  }
  return element;
}

/** A tenth of a second of silence, as a WAV in memory — the page's own, so nothing is fetched. */
function silentWav(): string {
  if (silence) return silence;
  const rate = 8000;
  const samples = rate / 10;
  const buffer = new ArrayBuffer(44 + samples);
  const view = new DataView(buffer);
  const text = (at: number, s: string) => [...s].forEach((c, i) => view.setUint8(at + i, c.charCodeAt(0)));
  text(0, 'RIFF');
  view.setUint32(4, 36 + samples, true);
  text(8, 'WAVE');
  text(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, rate, true);
  view.setUint32(28, rate, true);
  view.setUint16(32, 1, true);
  view.setUint16(34, 8, true);
  text(36, 'data');
  view.setUint32(40, samples, true);
  for (let i = 0; i < samples; i += 1) view.setUint8(44 + i, 128);
  silence = URL.createObjectURL(new Blob([buffer], { type: 'audio/wav' }));
  return silence;
}

/**
 * Called from inside a click. Plays the silence and stops it, leaving the
 * element allowed to play. Does nothing to an element already playing the
 * recording.
 */
export function unlockExamAudio(): void {
  const el = examAudio();
  if (el.src && el.src !== silence && !el.paused) return;
  const url = silentWav();
  el.src = url;
  const played = el.play();
  void played
    ?.then(() => {
      if (el.src === url) el.pause();
    })
    .catch(() => undefined);
}
